import React, { useId, useMemo, useRef, useState } from 'react';
import { AppIcon } from '../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';
import { useClickOutside } from '../../../hooks/useClickOutside';
import type { GoogleAdsConversionAction } from '../../../services/modules/googleAdsRepository';

const BADGES = [
    'badge-success',
    'badge-warning',
    'badge-info',
    'badge-brand',
    'badge-brand-dark',
    'badge-danger',
] as const;

type Props = {
    actions: GoogleAdsConversionAction[];
    selectedIds: string[];
    disabled?: boolean;
    loading?: boolean;
    onToggle: (id: string) => void;
};

const GoogleAdsConversionPicker: React.FC<Props> = ({
    actions,
    selectedIds,
    disabled,
    loading,
    onToggle,
}) => {
    const listId = useId();
    const searchId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    useClickOutside(rootRef, () => setIsOpen(false), isOpen);

    const selected = useMemo(
        () => actions.filter((action) => selectedIds.includes(action.id)),
        [actions, selectedIds]
    );
    const available = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return actions.filter((action) => {
            if (selectedIds.includes(action.id)) return false;
            return !needle || action.name.toLowerCase().includes(needle);
        });
    }, [actions, selectedIds, query]);

    return (
        <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="input-label mb-s1">Ações de conversão *</legend>
            <p className="mb-s3 text-caption text-secondary">
                Inclui tag global (site) e importação. No ganho, a venda vai para todas as selecionadas.
            </p>
            <div className="relative min-w-0" ref={rootRef}>
                <ul className="m-0 flex list-none flex-wrap items-stretch gap-s2 p-0">
                    {selected.map((action, index) => (
                        <li key={action.id} className="min-w-0 max-w-full">
                            <span
                                className={cn(
                                    'badge badge-sm inline-flex max-w-full items-center gap-s1',
                                    BADGES[index % BADGES.length]
                                )}
                                title={action.name}
                            >
                                <span className="min-w-0 truncate">{action.name}</span>
                                <button
                                    type="button"
                                    onClick={() => onToggle(action.id)}
                                    className="chip-remove min-h-11 min-w-11 shrink-0 md:min-h-4 md:min-w-4"
                                    disabled={disabled}
                                    aria-label={`Remover ${action.name}`}
                                >
                                    <AppIcon name="x" className="h-3 w-3" />
                                </button>
                            </span>
                        </li>
                    ))}
                    <li className="min-w-0 max-w-full sm:max-w-none">
                        <button
                            type="button"
                            disabled={disabled || loading}
                            onClick={() => setIsOpen((open) => !open)}
                            className="chip chip--dashed min-h-11 w-full max-w-full touch-manipulation sm:w-auto md:min-h-7"
                            aria-expanded={isOpen}
                            aria-controls={listId}
                            aria-haspopup="listbox"
                        >
                            <AppIcon name="plus" className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span>{loading ? 'Carregando…' : 'Adicionar ação'}</span>
                        </button>
                    </li>
                </ul>
                {isOpen && !disabled && (
                    <div
                        className="relative z-dropdown mt-s2 w-full min-w-0 rounded-xl border border-border bg-surface py-s1 shadow-lg md:absolute md:left-0 md:right-0 md:mt-s1"
                        role="dialog"
                        aria-label="Escolher ações de conversão"
                    >
                        <div className="border-b border-subtle px-s2 py-s2">
                            <label htmlFor={searchId} className="sr-only">Buscar ação</label>
                            <input
                                id={searchId}
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Buscar..."
                                autoComplete="off"
                                autoFocus
                                className="input min-h-11 w-full md:min-h-9 md:h-auto"
                            />
                        </div>
                        <ul
                            id={listId}
                            role="listbox"
                            aria-multiselectable="true"
                            className="m-0 max-h-[min(16rem,50dvh)] list-none overflow-y-auto py-s1 md:max-h-60"
                        >
                            {available.length === 0 ? (
                                <li className="px-s3 py-s3 text-center text-caption text-muted">
                                    {query ? 'Nenhuma encontrada' : 'Todas selecionadas'}
                                </li>
                            ) : (
                                available.map((action) => (
                                    <li key={action.id}>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected="false"
                                            className="flex min-h-11 w-full items-center px-s3 py-s2 text-left text-body-sm font-medium text-secondary hover:bg-surface-subtle focus-visible:bg-primary-subtle focus-visible:outline-none md:min-h-9"
                                            onClick={() => {
                                                onToggle(action.id);
                                                setQuery('');
                                            }}
                                        >
                                            <span className="min-w-0 whitespace-normal break-words md:truncate">
                                                {action.name}
                                            </span>
                                        </button>
                                    </li>
                                ))
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default GoogleAdsConversionPicker;
